import Box from '@mui/material/Box';
import Tooltip from '@mui/material/Tooltip';
import Typography from '@mui/material/Typography';
import { SystemStyleObject } from '@mui/system/styleFunctionSx/styleFunctionSx';
import React from 'react';

import InfoIcon from '@/assets/icons/info-icon.svg?react';

interface Props {
  readonly type: 'info';
  readonly iconPosition?: 'start' | 'end';
  readonly message: ReadonlyArray<string | React.ReactElement>;
  readonly tooltip?: string;
  readonly hasIcon?: boolean;
}

export const PaymentFlowModalMessage: React.FC<Props> = (
  props: Props,
): React.ReactElement | null => {
  const { iconPosition = 'start', message, tooltip = '', hasIcon = true } = props;

  const containerSx = React.useMemo((): SystemStyleObject | readonly SystemStyleObject[] => {
    if (iconPosition === 'end') {
      return {
        ...styles.container,
        flexDirection: 'row-reverse',
      };
    } else {
      return styles.container;
    }
  }, [iconPosition]);

  const iconSx = React.useMemo((): SystemStyleObject | readonly SystemStyleObject[] => {
    if (iconPosition === 'end') {
      return { ml: 'auto', height: 16, width: 16, color: 'custom.LightGray' };
    } else {
      return { height: 16, width: 16, color: 'custom.LightGray' };
    }
  }, [iconPosition]);

  if (!message.length) {
    return null;
  }

  return (
    <Box sx={containerSx}>
      {hasIcon && (
        <Box sx={iconSx}>
          {tooltip ? (
            <Tooltip title={tooltip} placement="top">
              <Box>
                <InfoIcon fill="currentColor" width={16} height={16} />
              </Box>
            </Tooltip>
          ) : (
            <InfoIcon fill="currentColor" width={16} height={16} />
          )}
        </Box>
      )}

      <Box display="flex" flexDirection="column" gap="2px">
        {message.map((line: string | React.ReactElement, key: number): React.ReactElement => {
          function Line(): React.ReactElement {
            if (typeof line === 'string') {
              return (
                <Typography variant="caption" lineHeight="20px" color="custom.LightGray">
                  {line}
                </Typography>
              );
            } else {
              return line;
            }
          }

          return <Line key={key} />;
        })}
      </Box>
    </Box>
  );
};

const styles: Record<string, SystemStyleObject> = {
  container: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'flex-start',
    gap: '12px',
    py: '16px',
    px: '20px',
    bgcolor: 'custom.Primary1Background',
    borderRadius: 4,
  },
};
